// When a listener's answer is COMPLETE, as opposed to merely first.
//
// WHY. Firestore's persistent cache (src/firebase/client.ts) answers a listener
// before the server does, and for a query that answer can be PART of the real
// one: the documents this browser has seen, without any written somewhere else
// since (a TPT import written by a Cloud Function, a product made on another
// device). Shown, it is the fastest thing available. Taken as the whole answer,
// it is wrong: the product editor read "loaded, and this product is not among
// yours" and sent the seller away with "Product not found" (5 of 6 fresh loads
// of a product made elsewhere, measured 2026-10-18), and the discount codes
// panel told the seller a code's products no longer existed.
//
// HOW. A listener that must know its answer is complete passes its callback
// through confirmedSnapshots and listens with `includeMetadataChanges: true`.
// Every snapshot that changes the documents reaches it at once, cache answers
// included, and `confirmed` turns true on the first snapshot the server
// answered, and stays true. includeMetadataChanges is what makes that answer
// arrive at all when the cache already held every document: without it a sync
// that changes nothing raises no event. Other metadata-only events (a local
// write's pending flag clearing, the connection dropping) are dropped, since a
// listener without the option never saw them either.
//
// Offline, the server never answers and `confirmed` stays false: the cached
// documents still show, and nothing treats them as all there is.
//
// Compare onSnapshotFromServer (src/firebase/firestore/server-first-snapshot.ts),
// which hides cache answers altogether, for a query the server already rendered.
//
// Pure: no Firestore import, so the rule is tested with plain objects.

/** What confirmedSnapshots reads off a Firestore QuerySnapshot. */
export type SnapshotLike = {
  metadata: { fromCache: boolean };
  /** The document changes since the last snapshot, metadata-only changes excluded. */
  docChanges: () => readonly unknown[];
};

/**
 * Wrap a listener's callback so it hears every snapshot that changes the
 * documents (the first one always) and the first one the server answered, even
 * when that changes nothing; `confirmed` says whether the server has answered
 * yet. For onSnapshot(q, { includeMetadataChanges: true }, ...). Each call
 * returns a fresh wrapper, so give every listener its own.
 */
export function confirmedSnapshots<S extends SnapshotLike>(
  onNext: (snapshot: S, confirmed: boolean) => void,
): (snapshot: S) => void {
  let first = true;
  let confirmed = false;
  return (snapshot) => {
    const confirmsNow = !confirmed && !snapshot.metadata.fromCache;
    if (!first && !confirmsNow && snapshot.docChanges().length === 0) return;
    first = false;
    if (confirmsNow) confirmed = true;
    onNext(snapshot, confirmed);
  };
}
